"use client"

import { Icons } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { ArrowLeft, Calendar, Home, MapPin, Search, Truck } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"

export default function NotFound() {
  const router = useRouter()
  const helpfulLinks = [
    {
      title: "Find Parking",
      description: "Search for available parking spaces near you",
      icon: Search,
      href: "/search",
    },
    {
      title: "My Bookings",
      description: "View and manage your parking reservations",
      icon: Calendar,
      href: "/bookings",
    },
    {
      title: "Nearby Areas",
      description: "Discover parking areas in your vicinity",
      icon: MapPin,
      href: "/search",
    },
    {
      title: "Vehicle Management",
      description: "Add or manage your vehicles",
      icon: Truck,
      href: "/vehicles",
    },
  ]

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-3xl w-full space-y-8">
        {/* Main Error Message */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <Icons.logo className="h-12 w-12 text-primary" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight">Page Not Found</h1>
          <p className="text-lg text-muted-foreground">
            We couldn't find the page you're looking for. But don't worry, we've
            got you covered!
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-4 justify-center">
          <Button asChild variant="default" size="lg" className="gap-2">
            <Link prefetch href="/">
              <Home className="h-4 w-4" />
              Go to Homepage
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="gap-2">
            <Link prefetch href="/search">
              <Search className="h-4 w-4" />
              Find Parking
            </Link>
          </Button>
          <Button
            variant="ghost"
            onClick={() => router.back()}
            size="lg"
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
        </div>

        {/* Helpful Links Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-center">
            You might find these helpful:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {helpfulLinks.map((link) => (
              <Link prefetch key={link.title} href={link.href}>
                <Card className="p-4 hover:bg-muted transition-colors cursor-pointer">
                  <div className="flex items-start space-x-4">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <link.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium">{link.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {link.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Support Section */}
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">
            Need assistance? We're here to help!
          </p>
          <div className="flex justify-center gap-4">
            <Button variant="link" asChild>
              <Link href="/contact">Contact Support</Link>
            </Button>
            <Button variant="link" asChild>
              <Link href="/faq">View FAQs</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
